import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { type RefObject, useCallback, useRef } from "react";
import { type BaseAppNode } from "./BaseAppNode";
import { markNodeRemoval } from "./nodeRemovalTracking";

interface WindowDecoratorProps<T extends BaseAppNode> {
  node: T;
  children: (handleDelete: () => void, ref?: RefObject<HTMLDivElement>) => React.ReactNode;
  className?: string;
  decoratorRef?: RefObject<HTMLDivElement>;
  onBeforeDelete?: () => void | Promise<void>;
}

/**
 * WindowDecorator - React component wrapper for nodes that need:
 * - X delete button
 * - Common window-like styling
 *
 * This is NOT a Lexical Node class - it's a React decorator component.
 */
export function WindowDecorator<T extends BaseAppNode>({
  node,
  children,
  className,
  decoratorRef,
  onBeforeDelete,
}: WindowDecoratorProps<T>) {
  const [editor] = useLexicalComposerContext();
  const ref = useRef<HTMLDivElement>(null);
  const mergedRef = decoratorRef || ref;

  const handleDelete = useCallback(async () => {
    // Execute special pre-delete handling (UI layer callback)
    if (onBeforeDelete) {
      await onBeforeDelete();
    }

    // Mark deletion + execute deletion (UI layer)
    markNodeRemoval(editor);
    editor.update(() => {
      const currentNode = editor.getEditorState()._nodeMap.get(node.getKey());
      if (currentNode) {
        currentNode.remove();
      }
    });
  }, [editor, node, onBeforeDelete]);

  return (
    <div ref={mergedRef} className={className}>
      {children(handleDelete, mergedRef)}
    </div>
  );
}
